import { ZoneFormGeometryTypeEnum } from '@components/cockpit/panels/zoneManagement/zoneManagement.types';
import { FormatMessageType } from '@hooks/useValidation';
import { LIST, MIN_FIELD, REQUIRED_FIELD } from '@utils/validation/validation.constant';
import Yup from '@utils/validation/yup-extended';

import { Zone2DTypeEnum } from '@/types/config/config.types';
import { ProcedureModel } from '@/types/config/procedure.types';
import { ZoneFormDisplayedFormatEnum } from '@/types/map.types';

import { polygonSchema } from './polygon.schema';

export function zoneSchema(formatMessage: FormatMessageType) {
  return Yup.object({
    name: Yup.string().required(
      formatMessage(
        { id: REQUIRED_FIELD },
        { field: formatMessage({ id: 'components.zones.form.name' }).toLowerCase() },
      ),
    ),

    type: Yup.mixed<Zone2DTypeEnum>()
      .oneOf(Object.values(Zone2DTypeEnum) as Zone2DTypeEnum[], formatMessage({ id: LIST }))
      .required(
        formatMessage(
          { id: REQUIRED_FIELD },
          { field: formatMessage({ id: 'components.zones.form.type' }).toLowerCase() },
        ),
      ),

    platform: Yup.string().when('type', {
      is: (type: Zone2DTypeEnum) =>
        [Zone2DTypeEnum.NFZ_INFO, Zone2DTypeEnum.NFZ_WARNING, Zone2DTypeEnum.NFZ_CRITICAL].includes(type),
      then: (schema) =>
        schema.required(
          formatMessage(
            { id: REQUIRED_FIELD },
            { field: formatMessage({ id: 'components.zones.form.platform' }).toLocaleLowerCase() },
          ),
        ),
    }),

    geometry: Yup.mixed<ZoneFormGeometryTypeEnum>()
      .oneOf(Object.values(ZoneFormGeometryTypeEnum) as ZoneFormGeometryTypeEnum[], formatMessage({ id: LIST }))
      .required(
        formatMessage(
          { id: REQUIRED_FIELD },
          { field: formatMessage({ id: 'components.zones.form.geometry.title' }).toLowerCase() },
        ),
      ),

    procedureModel: Yup.mixed<ProcedureModel>().when('type', ([type], schema) =>
      [Zone2DTypeEnum.NFZ_INFO, Zone2DTypeEnum.NFZ_WARNING, Zone2DTypeEnum.NFZ_CRITICAL].includes(type)
        ? schema.required(
            formatMessage(
              { id: REQUIRED_FIELD },
              { field: formatMessage({ id: 'components.zones.form.procedureModel' }).toLowerCase() },
            ),
          )
        : schema.notRequired(),
    ),

    marginSize: Yup.number().when('type', {
      is: (value: Zone2DTypeEnum) => value === Zone2DTypeEnum.IMZ,
      then: (schema) =>
        schema
          .required(
            formatMessage(
              { id: REQUIRED_FIELD },
              { field: formatMessage({ id: 'components.zones.form.marginSize' }).toLocaleLowerCase() },
            ),
          )
          .min(
            0,
            formatMessage(
              { id: MIN_FIELD },
              {
                value: 0,
                field: formatMessage({ id: 'components.zones.form.marginSize' }).toLocaleLowerCase(),
              },
            ),
          ),
    }),

    lowerValue: Yup.number().when(['geometry', 'displayedFormat'], ([geometry, displayedFormat], schema) =>
      geometry === ZoneFormGeometryTypeEnum.INFINITE_HEIGHT
        ? schema
        : schema.required(
            formatMessage(
              { id: REQUIRED_FIELD },
              {
                field: formatMessage(
                  { id: 'components.zones.form.lowerValue' },
                  { isHeight: displayedFormat === ZoneFormDisplayedFormatEnum.HEIGHT },
                ),
              },
            ),
          ),
    ),

    upperValue: Yup.number().when(
      ['geometry', 'displayedFormat', 'lowerValue'],
      ([geometry, displayedFormat, lowerValue], schema) => {
        const minRequirement = !isNaN(lowerValue) ? lowerValue + 1 : -Infinity;
        const isHeight = displayedFormat === ZoneFormDisplayedFormatEnum.HEIGHT;
        return geometry === ZoneFormGeometryTypeEnum.INFINITE_HEIGHT
          ? schema
          : schema
              .min(minRequirement, formatMessage({ id: 'components.zones.form.upperValueError' }, { isHeight }))
              .required(
                formatMessage(
                  { id: REQUIRED_FIELD },
                  {
                    field: formatMessage({ id: 'components.zones.form.upperValue' }, { isHeight }),
                  },
                ),
              );
      },
    ),

    polygon: polygonSchema(formatMessage),
  });
}
